import type { ComponentConfigV2, QuizOptions, QuizPurpose } from '@/types/configurationV2'
import {
  applyAnswerFilterChange,
  isAnswerExplanationLocked,
  isBlockLocationDisabled,
  shouldShowShortageStrategy,
  suggestLabelForFilter,
  type AnswerFilter,
} from './quizAnswerFilterGuards'

interface QuizOptionsEditorProps {
  component: ComponentConfigV2
  onUpdate: (updated: ComponentConfigV2) => void
}

const QUIZ_PURPOSES: { value: QuizPurpose; label: string }[] = [
  { value: 'selected', label: 'selected' },
  { value: 'review', label: 'review' },
  { value: 'block', label: 'block' },
  { value: 'checkup', label: 'checkup' },
  { value: 'applied', label: 'applied' },
  { value: 'wrapup', label: 'wrapup' },
  { value: 'aiq', label: 'aiq' },
]

export default function QuizOptionsEditor({ component, onUpdate }: QuizOptionsEditorProps) {
  const options = component.options as QuizOptions

  const update = (patch: Partial<QuizOptions>) => {
    onUpdate({ ...component, options: { ...options, ...patch } })
  }

  const updateExtraction = (patch: Partial<QuizOptions['extraction']>) => {
    update({ extraction: { ...options.extraction, ...patch } })
  }

  const updateCount = (patch: Partial<QuizOptions['count']>) => {
    update({ count: { ...options.count, ...patch } })
  }

  const updateCompletion = (patch: Partial<QuizOptions['completion']>) => {
    update({ completion: { ...options.completion, ...patch } })
  }

  const toggleContentScope = (scope: 'current' | 'previous' | 'next') => {
    const current = options.extraction.contentScope
    const next = current.includes(scope)
      ? current.filter((s) => s !== scope)
      : [...current, scope]
    updateExtraction({ contentScope: next.length > 0 ? next : ['current'] })
  }

  const toggleQuizLocation = (loc: QuizPurpose) => {
    // wrong_in_review 에선 block 은 오답 풀기 대상이 아니므로 추가를 막는다(체크박스도 비활성).
    if (loc === 'block' && isBlockLocationDisabled(options.extraction.answerFilter)) return
    const current = options.extraction.quizLocations
    const next = current.includes(loc) ? current.filter((l) => l !== loc) : [...current, loc]
    updateExtraction({ quizLocations: next })
  }

  const selectAnswerFilter = (filter: AnswerFilter) => {
    const extraction = applyAnswerFilterChange(options.extraction, filter)
    const suggestedLabel = suggestLabelForFilter(filter, component.label)
    onUpdate({
      ...component,
      ...(suggestedLabel ? { label: suggestedLabel } : {}),
      options: { ...options, extraction },
    })
  }

  const answerFilter = options.extraction.answerFilter
  const explanationLocked = isAnswerExplanationLocked(answerFilter)
  const blockDisabled = isBlockLocationDisabled(answerFilter)

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Panel 1: 추출 범위 */}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-bold text-purple-600 mb-2">추출 범위</div>

        <div className="mb-2">
          <div className="text-xs text-gray-500 mb-1">소속 위치</div>
          {(['current', 'previous', 'next'] as const).map((scope) => (
            <label key={scope} className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={options.extraction.contentScope.includes(scope)}
                onChange={() => toggleContentScope(scope)}
              />
              {{ current: '현재', previous: '직전', next: '다음' }[scope]}
            </label>
          ))}
        </div>

        <div className="mb-2">
          <div className="text-xs text-gray-500 mb-1">퀴즈 목적 (quiz_location)</div>
          <div className="flex flex-wrap gap-1">
            {QUIZ_PURPOSES.map(({ value, label }) => {
              const disabled = value === 'block' && blockDisabled
              return (
                <label
                  key={value}
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    disabled ? 'bg-gray-100 text-gray-400' : 'bg-purple-50'
                  }`}
                  title={disabled ? '블럭퀴즈는 오답 풀기 대상이 아니에요' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={options.extraction.quizLocations.includes(value)}
                    onChange={() => toggleQuizLocation(value)}
                    disabled={disabled}
                    className="mr-1"
                  />
                  {label}
                </label>
              )
            })}
          </div>
          {blockDisabled && (
            <div className="text-xs text-gray-400 mt-1">블럭퀴즈는 오답 풀기 대상이 아니에요</div>
          )}
        </div>

        <div>
          <div className="text-xs text-gray-500 mb-1">정오답 여부</div>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`ans-${component.id}`}
              checked={answerFilter === 'all'}
              onChange={() => selectAnswerFilter('all')}
            />
            전체
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`ans-${component.id}`}
              checked={answerFilter === 'incorrect'}
              onChange={() => selectAnswerFilter('incorrect')}
            />
            오답만
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`ans-${component.id}`}
              checked={answerFilter === 'wrong_in_review'}
              onChange={() => selectAnswerFilter('wrong_in_review')}
            />
            오답 풀기(이번 복습 회차 오답)
          </label>
          {answerFilter === 'wrong_in_review' && (
            <div className="mt-1 text-xs text-gray-400">
              복습 레슨 마지막 단계용 — 이번 복습 회차 오답만 재풀이(블럭퀴즈 제외·해설 항상 노출).
            </div>
          )}
        </div>
      </div>

      {/* Panel 2: 추출 개수 & 방법 */}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-bold text-purple-600 mb-2">추출 개수 & 방법</div>

        <div className="mb-2">
          <div className="text-xs text-gray-500 mb-1">개수</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={options.count.value === 'max' ? '' : options.count.value}
              disabled={options.count.value === 'max'}
              onChange={(e) => updateCount({ value: parseInt(e.target.value) || 1 })}
              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100"
            />
            <label className="text-xs">
              <input
                type="checkbox"
                checked={options.count.value === 'max'}
                onChange={(e) => updateCount({ value: e.target.checked ? 'max' : 5 })}
                className="mr-1"
              />
              Max
            </label>
          </div>
        </div>

        {shouldShowShortageStrategy(answerFilter) && (
          <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
            <div className="text-xs text-orange-700 mb-1">오답 부족 시</div>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="radio"
                name={`short-${component.id}`}
                checked={options.count.shortageStrategy?.action !== 'fill'}
                onChange={() => updateCount({ shortageStrategy: { action: 'available_only' } })}
              />
              있는 것만
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="radio"
                name={`short-${component.id}`}
                checked={options.count.shortageStrategy?.action === 'fill'}
                onChange={() =>
                  updateCount({ shortageStrategy: { action: 'fill', fillMethod: 'from_correct' } })
                }
              />
              채우기
            </label>
            {options.count.shortageStrategy?.action === 'fill' && (
              <div className="ml-4 mt-1">
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    name={`fill-${component.id}`}
                    checked={options.count.shortageStrategy?.fillMethod === 'from_correct'}
                    onChange={() =>
                      updateCount({ shortageStrategy: { action: 'fill', fillMethod: 'from_correct' } })
                    }
                  />
                  오답 아닌 문제로
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    name={`fill-${component.id}`}
                    checked={options.count.shortageStrategy?.fillMethod === 'from_previous_incorrect'}
                    onChange={() =>
                      updateCount({ shortageStrategy: { action: 'fill', fillMethod: 'from_previous_incorrect' } })
                    }
                  />
                  직전 레슨 오답에서
                </label>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="text-xs text-gray-500 mb-1">추출 방법</div>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`ord-${component.id}`}
              checked={options.ordering === 'random'}
              onChange={() => update({ ordering: 'random' })}
            />
            Random
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`ord-${component.id}`}
              checked={options.ordering === 'sequential'}
              onChange={() => update({ ordering: 'sequential' })}
            />
            순서대로 (order_in_location)
          </label>
        </div>
      </div>

      {/* Panel 3: 변수 & 풀이 방식 */}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-bold text-purple-600 mb-2">변수 & 풀이 방식</div>

        <div className="mb-2">
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={options.useFixedVars}
              onChange={(e) => update({ useFixedVars: e.target.checked })}
            />
            변수문제 사용 안함 (고정 출제)
          </label>
          <div className="text-xs text-gray-400 ml-5">vars_with_sample_value 사용</div>
        </div>

        <div className="mb-2">
          <div className="text-xs text-gray-500 mb-1">문제 풀이 방식</div>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`solve-${component.id}`}
              checked={options.solveMode === 'lesson'}
              onChange={() => update({ solveMode: 'lesson' })}
            />
            Lesson용
          </label>
          <div className="text-xs text-gray-400 ml-5">Skip ✕ · 반복제출 ✕ · 즉시 정오답 ○</div>
          <label className="flex items-center gap-1.5 text-xs mt-1">
            <input
              type="radio"
              name={`solve-${component.id}`}
              checked={options.solveMode === 'test'}
              onChange={() => update({ solveMode: 'test' })}
            />
            Test용
          </label>
          <div className="text-xs text-gray-400 ml-5">Skip ○ · 반복제출 ○ · 즉시 정오답 ✕</div>
        </div>

        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={options.showHint}
            onChange={(e) => update({ showHint: e.target.checked })}
          />
          힌트 제공
        </label>

        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={options.offlineInput === true}
              onChange={(e) => update({ offlineInput: e.target.checked })}
            />
            교재 풀이 후 패드 입력 (채점 보류)
          </label>
          <div className="text-xs text-gray-400 ml-5">
            체크업북 트랙·1회차·비복습에서만 적용. 자동채점 없이 입력만 저장 → 강사가 코칭 콘솔에서 채점.
          </div>
        </div>

        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-1">오답 해설 노출</div>
          {explanationLocked ? (
            <div className="text-xs text-gray-500">
              해설 항상 노출 (백엔드 강제)
              <div className="text-xs text-gray-400">
                오답 풀기는 백엔드가 해설을 always 로 강제하므로 정책 편집이 무의미합니다.
              </div>
            </div>
          ) : (
            <>
              <label className="flex items-center gap-1.5 text-xs">
                <input
                  type="radio"
                  name={`answer-expl-${component.id}`}
                  checked={(options.answerExplanationPolicy ?? 'always') === 'always'}
                  onChange={() => update({ answerExplanationPolicy: 'always' })}
                />
                항상 노출
              </label>
              <label className="flex items-center gap-1.5 text-xs mt-1">
                <input
                  type="radio"
                  name={`answer-expl-${component.id}`}
                  checked={options.answerExplanationPolicy === 'hide_on_incorrect'}
                  onChange={() => update({ answerExplanationPolicy: 'hide_on_incorrect' })}
                />
                오답 시 숨김
              </label>
              <div className="text-xs text-gray-400 ml-5">오답풀기·리뷰노트 보호 (정답은 해설 노출)</div>
            </>
          )}
        </div>

        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={options.choiceDisplayPolicy === 'dimmed'}
              onChange={(e) => update({ choiceDisplayPolicy: e.target.checked ? 'dimmed' : 'show' })}
            />
            선택지 텍스트 흐리기 (보기 가림)
          </label>
          <div className="text-xs text-gray-400 ml-5">
            1회차(책 풀이)에서만 선택지 텍스트를 opacity 0.05로 흐립니다. 번호·이미지·히트영역은 유지.
            객관식·인라인만 적용(순서·짝짓기·끌어채우기·인라인택1은 노출 유지). 복습 회차는 정상 노출.
          </div>
        </div>
      </div>

      {/* Panel 4: 완료 조건 */}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-bold text-purple-600 mb-2">성과 측정 & 완료 조건</div>
        <div className="text-xs text-gray-400 mb-2">점수 = Σ(문제별 점수) / 문제수 × 100%</div>

        <div className="mb-2">
          <div className="text-xs text-gray-500 mb-1">패스 정답률</div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={100}
              value={options.completion.passingScore}
              onChange={(e) => updateCompletion({ passingScore: parseInt(e.target.value) || 0 })}
              className="w-14 px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <span className="text-xs">%</span>
          </div>
        </div>

        <div>
          <div className="text-xs text-gray-500 mb-1">최대 시도 횟수</div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              value={options.completion.maxAttempts}
              onChange={(e) => updateCompletion({ maxAttempts: parseInt(e.target.value) || 1 })}
              className="w-14 px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <span className="text-xs">회</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">미달 시 재시도, 최대 횟수까지</div>
        </div>
      </div>
    </div>
  )
}
